import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, openingHoursSchema } from "../useBusinessInfo";
import "./group-tours.css";

/**
 * Group & School Tours — redesigned public page (route: /group-tours).
 *
 * Functional ground truth: frontend/src/pages/GroupToursPage.js (fully static —
 * SEO head + JSON-LD schema, react-router links to /contact and
 * /group-accommodation). Visual ground truth: design-ref "Group Tours.html".
 */

const baseSchema = {
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  name: "Group Tours - Umoona Opal Mine",
  description:
    "Educational group tours and school excursions at Umoona Opal Mine, Coober Pedy. Customized underground mining experiences for schools and large groups.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "14 Hutchison St",
    addressLocality: "Coober Pedy",
    addressRegion: "SA",
    postalCode: "5723",
    addressCountry: "AU",
  },
  url: "https://umoonaopalmine.com.au/group-tours",
};

const tourSteps = [
  {
    kicker: "01 · INCLUDED",
    kickerClass: "",
    title: "Coober Pedy town tour",
    text: "A hostess meets your group and tours Coober Pedy — including the underground churches and other sights.",
  },
  {
    kicker: "02 · INCLUDED",
    kickerClass: "gt-kick--teal",
    title: "Documentary & demonstration",
    text: 'We show the award-winning "Story of Opal" and a hands-on cutting & polishing demonstration.',
  },
  {
    kicker: "03 · INCLUDED",
    kickerClass: "gt-kick--turq",
    title: "Underground homes & mine",
    text: "We take your group into our underground homes and then deep into the original opal mine.",
  },
  {
    kicker: "04 · INCLUDED",
    kickerClass: "gt-kick--blue",
    title: "Shopping & refreshments",
    text: "Browse the displays and shop for opals in a relaxed atmosphere with complimentary tea or coffee.",
  },
];

const features = [
  {
    kicker: "EXTENDED",
    kickerClass: "",
    title: "1.5-hour experience",
    text: "Comprehensive coverage of all the major attractions.",
  },
  {
    kicker: "CUSTOM",
    kickerClass: "gt-kick--teal",
    title: "Any group size",
    text: "Tailored experiences for both small and large groups.",
  },
];

export default function RGroupToursPage() {
  const hours = useOpeningHours();
  const schemaData = { ...baseSchema, ...openingHoursSchema(hours) };
  return (
    <MarketingShell pageClass="pg-group-tours">
      <SEOHead
        title="School & Group Tours - Educational Mining Experience | Umoona Opal Mine"
        description="Educational group tours and school excursions at Umoona Opal Mine, Coober Pedy. Customized underground mining experiences for schools and large groups."
        keywords="group tours, school tours, educational tours, Coober Pedy group bookings, mining excursions"
        canonical="/group-tours"
        schemaData={schemaData}
      />

      {/* HERO */}
      <div className="u-hero">
        <img
          className="u-hero__img"
          src="/design/assets/group-hero.jpg"
          alt="Group Tours at Umoona Opal Mine"
        />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">
            Extended &amp; custom tours · small &amp; big groups
          </div>
          <h1 className="u-hero__title">Group Tours at Umoona</h1>
          <p className="u-hero__sub">
            The Umoona Opal Mine &amp; Museum offers extended and custom tours
            to suit groups of every size — an educational experience for
            schools and travellers alike.
          </p>
        </div>
      </div>

      {/* HOW OUR GROUP TOURS WORK — 4 steps */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">How our group tours work</div>
        <h2 className="u-h2">
          The complete tour takes approximately 1.5 hours and includes several
          unique experiences.
        </h2>
        <div className="u-grid2">
          {tourSteps.map((step) => (
            <div className="u-card gt-card" key={step.kicker}>
              <div className={"u-card__kicker " + step.kickerClass}>
                {step.kicker}
              </div>
              <div className="u-card__title">{step.title}</div>
              <p className="u-card__text">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE OUR GROUP TOURS */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Why choose our group tours</div>
        <h2 className="u-h2">Built for schools, coaches &amp; clubs</h2>
        <div className="u-grid3 gt-gallery">
          <div className="gt-phototile">
            <img src="/design/assets/group-step1.jpg" alt="Group tour exploring the underground opal mine at Umoona, Coober Pedy" loading="lazy" decoding="async" />
          </div>
          <div className="gt-phototile">
            <img src="/design/assets/group-step2.jpg" alt="Visitors learning about opal mining on a guided group tour underground" loading="lazy" decoding="async" />
          </div>
          <div className="gt-phototile">
            <img src="/design/assets/group-step4.jpg" alt="Tour group inside the underground tunnels at Umoona Opal Mine" loading="lazy" decoding="async" />
          </div>
        </div>
        <div className="gt-featurewrap">
          <div className="u-grid2">
            {features.map((feature) => (
              <div className="u-card gt-card" key={feature.kicker}>
                <div className={"u-card__kicker " + feature.kickerClass}>
                  {feature.kicker}
                </div>
                <div className="u-card__title">{feature.title}</div>
                <p className="u-card__text">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STAY UNDERGROUND — group accommodation teaser */}
      <section className="u-split">
        <div className="gt-accompanel">
          <div className="u-eyebrow">Stay underground</div>
          <h2 className="u-h2">Group accommodation</h2>
          <p className="gt-accomtext">
            Complete your group experience with our unique underground
            accommodation — where the temperature is a constant 24°C
            year-round.
          </p>
          <div className="gt-stats">
            <div>
              <div className="gt-stat__num gt-stat__num--flame">102</div>
              <div className="gt-stat__label">Max beds</div>
            </div>
            <div>
              <div className="gt-stat__num gt-stat__num--turq">24°C</div>
              <div className="gt-stat__label">Constant</div>
            </div>
            <div>
              <div className="gt-stat__num gt-stat__num--teal">4–6</div>
              <div className="gt-stat__label">Per room</div>
            </div>
          </div>
          <Link to="/group-accommodation" className="gt-accombtn">
            Learn About Accommodation →
          </Link>
        </div>
        <div className="u-photoband">
          <img
            src="/design/assets/accom-1.jpg"
            alt="Underground group accommodation at Umoona"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to book your group tour?</h2>
          <p className="u-ctacenter__sub">
            Contact us to arrange your extended group tour experience at
            Australia's most iconic underground opal mine.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/contact" className="u-btn u-btn--primary">
              Enquire Now
            </Link>
            <Link to="/group-accommodation" className="u-btn u-btn--ghost">
              Group Accommodation
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
